/**
 * TanStack Query hooks for the versioning API — branches, commits, diff, merge.
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  fetchBranches,
  createBranch,
  deleteBranch,
  renameBranch,
  moveNodes,
  fetchCommits,
  createCommit,
  fetchUncommitted,
  diffBranches,
  mergePreview,
  applyMerge,
  applyMergeResolved,
  fetchTags,
  createTag,
  deleteTag,
  squashCommits,
  type Branch,
  type Commit,
  type DiffEntry,
  type MergePreviewEntry,
  type MergeResult,
  type UncommittedSummary,
  type Tag,
  type SquashResult,
} from "../api/versioning";
import { useIsVisible } from "./useIsVisible";

export function useBranches(projectId: string) {
  return useQuery<Branch[]>({
    queryKey: ["branches", projectId],
    queryFn: () => fetchBranches(projectId),
  });
}

export function useCreateBranch(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ name, parentBranch }: { name: string; parentBranch?: string }) =>
      createBranch(projectId, name, parentBranch),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["branches", projectId] });
    },
  });
}

export function useDeleteBranch(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (branchName: string) => deleteBranch(projectId, branchName),
    onMutate: async (branchName) => {
      await qc.cancelQueries({ queryKey: ["branches", projectId] });
      const previous = qc.getQueryData<Branch[]>(["branches", projectId]);
      qc.setQueryData<Branch[]>(
        ["branches", projectId],
        (old) => old?.filter((b) => b.name !== branchName) ?? [],
      );
      return { previous };
    },
    onError: (_err, _branchName, context) => {
      if (context?.previous) {
        qc.setQueryData(["branches", projectId], context.previous);
      }
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ["branches", projectId] });
    },
  });
}

export function useRenameBranch(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ oldName, newName }: { oldName: string; newName: string }) =>
      renameBranch(projectId, oldName, newName),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["branches", projectId] });
    },
  });
}

export function useMoveNodes(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ nodeIds, targetBranch }: { nodeIds: string[]; targetBranch: string }) =>
      moveNodes(projectId, nodeIds, targetBranch),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["run-table", projectId] });
      qc.invalidateQueries({ queryKey: ["nodes", projectId] });
      qc.invalidateQueries({ queryKey: ["provenance", projectId] });
    },
  });
}

export function useCommits(projectId: string, branch: string) {
  return useQuery<Commit[]>({
    queryKey: ["commits", projectId, branch],
    queryFn: () => fetchCommits(projectId, branch),
  });
}

export function useCreateCommit(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ branch, message }: { branch: string; message: string }) =>
      createCommit(projectId, branch, message),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: ["commits", projectId, variables.branch] });
      qc.invalidateQueries({ queryKey: ["uncommitted", projectId, variables.branch] });
    },
  });
}

export function useUncommitted(projectId: string, branch: string, enabled = true) {
  const isVisible = useIsVisible();
  const isPollingEnabled = enabled && isVisible;

  return useQuery<UncommittedSummary>({
    queryKey: ["uncommitted", projectId, branch],
    queryFn: () => fetchUncommitted(projectId, branch),
    enabled: isPollingEnabled,
    refetchInterval: isPollingEnabled ? 30_000 : false,
  });
}

export function useDiffBranches(
  projectId: string,
  branchA: string,
  branchB: string,
  enabled = false,
) {
  return useQuery<DiffEntry[]>({
    queryKey: ["diff", projectId, branchA, branchB],
    queryFn: () => diffBranches(projectId, branchA, branchB),
    enabled,
  });
}

export function useMergePreview(
  projectId: string,
  source: string,
  target: string,
  enabled = false,
) {
  return useQuery<MergePreviewEntry[]>({
    queryKey: ["merge-preview", projectId, source, target],
    queryFn: () => mergePreview(projectId, source, target),
    enabled,
  });
}

export function useApplyMerge(projectId: string) {
  const qc = useQueryClient();
  return useMutation<
    MergeResult,
    Error,
    { source: string; target: string; force?: boolean }
  >({
    mutationFn: ({ source, target, force }) =>
      applyMerge(projectId, source, target, force),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: ["branches", projectId] });
      qc.invalidateQueries({ queryKey: ["commits", projectId] });
      qc.invalidateQueries({ queryKey: ["nodes", projectId] });
      qc.invalidateQueries({ queryKey: ["provenance", projectId] });
      qc.invalidateQueries({
        queryKey: ["diff", projectId, variables.source, variables.target],
      });
    },
  });
}

export function useApplyMergeResolved(projectId: string) {
  const qc = useQueryClient();
  return useMutation<
    MergeResult,
    Error,
    { source: string; target: string; resolutions: Record<string, "source" | "target"> }
  >({
    mutationFn: ({ source, target, resolutions }) =>
      applyMergeResolved(projectId, source, target, resolutions),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: ["branches", projectId] });
      qc.invalidateQueries({ queryKey: ["commits", projectId] });
      qc.invalidateQueries({ queryKey: ["nodes", projectId] });
      qc.invalidateQueries({ queryKey: ["provenance", projectId] });
      qc.invalidateQueries({
        queryKey: ["diff", projectId, variables.source, variables.target],
      });
    },
  });
}

// -- Tags --

export function useTags(projectId: string) {
  return useQuery<Tag[]>({
    queryKey: ["tags", projectId],
    queryFn: () => fetchTags(projectId),
  });
}

export function useCreateTag(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      name,
      commitId,
      branch,
      message,
    }: {
      name: string;
      commitId: string;
      branch: string;
      message?: string;
    }) => createTag(projectId, name, commitId, branch, message),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["tags", projectId] });
    },
  });
}

export function useDeleteTag(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (name: string) => deleteTag(projectId, name),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["tags", projectId] });
    },
  });
}

// -- Squash --

export function useSquashCommits(projectId: string) {
  const qc = useQueryClient();
  return useMutation<
    SquashResult,
    Error,
    { branch: string; message: string; keepLast?: number }
  >({
    mutationFn: ({ branch, message, keepLast }) =>
      squashCommits(projectId, branch, message, keepLast),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: ["commits", projectId, variables.branch] });
    },
  });
}
